import { useState, useCallback } from 'react'
import { generateSymmetryPuzzle, PALETTES, type SymmetryMode, type Difficulty } from './symmetryData'

export type { SymmetryMode, Difficulty }

export interface SymmetryGameState {
  rows:          number
  cols:          number
  solution:      string[][]
  isGiven:       boolean[][]
  userGrid:      string[][]   // given cells pre-filled; editable cells start 'empty'
  palette:       string[]
  selectedColor: string       // active paint color; 'empty' = eraser
  moves:         number
  startTime:     number | null
  endTime:       number | null
  gameComplete:  boolean
}

function initState(difficulty: Difficulty, mode: SymmetryMode): SymmetryGameState {
  const puzzle  = generateSymmetryPuzzle(difficulty, mode)
  const palette = PALETTES[difficulty]

  const userGrid = puzzle.solution.map((row, r) =>
    row.map((color, c) => (puzzle.isGiven[r][c] ? color : '#FFFFFF')),
  )

  return {
    ...puzzle,
    userGrid,
    palette,
    selectedColor: palette[0],
    moves:         0,
    startTime:     null,
    endTime:       null,
    gameComplete:  false,
  }
}

export function useSymmetry(difficulty: Difficulty, mode: SymmetryMode) {
  const [state, setState] = useState<SymmetryGameState>(() => initState(difficulty, mode))

  const resetGame = useCallback(() => {
    setState(initState(difficulty, mode))
  }, [difficulty, mode])

  const selectColor = useCallback((color: string) => {
    setState((prev) => ({ ...prev, selectedColor: color }))
  }, [])

  const paintCell = useCallback((row: number, col: number) => {
    setState((prev) => {
      if (prev.isGiven[row][col] || prev.gameComplete) return prev

      const startTime = prev.startTime ?? Date.now()
      const newGrid   = prev.userGrid.map((r) => [...r])
      newGrid[row][col] = prev.selectedColor
      const moves = prev.moves + 1

      const gameComplete = newGrid.every((rowArr, r) =>
        rowArr.every((color, c) => prev.isGiven[r][c] || color === prev.solution[r][c]),
      )

      return {
        ...prev,
        userGrid: newGrid,
        moves,
        startTime,
        gameComplete,
        endTime: gameComplete ? Date.now() : prev.endTime,
      }
    })
  }, [])

  return { state, resetGame, selectColor, paintCell }
}
